import React, { createContext, useContext, useState, useEffect } from 'react';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { auth } from '../firebase/config';
import { User, Role } from '../types/user';

interface AuthContextType {
  currentUser: User | null;
  originalUser: User | null; // L'utilisateur admin original lors de l'impersonation
  isImpersonating: boolean;
  setUser: (user: User | null) => void;
  loading: boolean;
  logout: () => Promise<void>;
  startImpersonation: (targetUser: User) => void;
  stopImpersonation: () => void;
}

const AuthContext = createContext<AuthContextType>({
  currentUser: null,
  originalUser: null,
  isImpersonating: false,
  setUser: () => {},
  loading: true,
  logout: async () => {},
  startImpersonation: () => {},
  stopImpersonation: () => {},
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [originalUser, setOriginalUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // Fonction pour déconnecter l'utilisateur
  const firebaseLogout = async () => {
    if (auth) {
      await signOut(auth);
    }
  };

  // État d'impersonation
  const isImpersonating = Boolean(originalUser);
  console.log('AuthProvider render - states:', { 
    currentUser: currentUser?.email, 
    originalUser: originalUser?.email, 
    isImpersonating 
  });

  // Fonction pour démarrer l'impersonation
  const startImpersonation = (targetUser: User) => {
    console.log('startImpersonation called', { targetUser, currentUser, originalUser });
    
    if (!currentUser) {
      console.log('No current user - cannot start impersonation');
      return;
    }
    
    // Vérifier que l'utilisateur actuel est admin
    if (currentUser.role !== Role.ADMIN) {
      console.error('Only admin users can impersonate other users');
      return;
    }

    console.log('Admin check passed, starting impersonation...');
    
    // Sauvegarder l'utilisateur original
    setOriginalUser(currentUser);
    console.log('Original user saved:', currentUser);
    
    // Changer l'utilisateur actuel vers l'utilisateur cible
    setCurrentUser(targetUser);
    console.log('Current user changed to:', targetUser);
    
    // Sauvegarder l'ID de l'utilisateur impersonné dans localStorage
    localStorage.setItem('impersonatedUserId', targetUser.id.toString());
    console.log('Impersonated user ID saved to localStorage:', targetUser.id);
    
    console.log(`Impersonation started: ${currentUser.name} is now acting as ${targetUser.name}`);
  };

  // Fonction pour arrêter l'impersonation
  const stopImpersonation = () => {
    console.log('🔴 stopImpersonation CALLED!');
    console.log('stopImpersonation called', { originalUser, currentUser });
    
    if (!originalUser) {
      console.log('No original user to restore');
      return;
    }
    
    console.log(`Impersonation stopped: returning to ${originalUser.name}`);
    
    // Créer une copie de l'utilisateur original pour éviter les références partagées
    const userToRestore = { ...originalUser };
    
    // D'abord effacer originalUser pour sortir du mode impersonation
    setOriginalUser(null);
    
    // Nettoyer l'ID utilisateur impersonné du localStorage
    localStorage.removeItem('impersonatedUserId');
    console.log('Impersonated user ID removed from localStorage');
    
    // Puis restaurer l'utilisateur immédiatement
    setCurrentUser(userToRestore);
    console.log('User restored to:', userToRestore);
    
    console.log('Impersonation state after stop:', { 
      newCurrentUser: userToRestore, 
      newOriginalUser: null,
      newIsImpersonating: false 
    });
  };

  // Fonction pour vérifier l'utilisateur avec le backend
  const verifyUserWithBackend = async (firebaseUser: any) => {
    try {
      // Obtenir le token ID
      const token = await firebaseUser.getIdToken();
      
      // Vérifier avec le backend
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/auth/verify`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        // Utiliser les données venant du backend (Prisma)
        return data.user;
      }
      
      return null;
    } catch (error) {
      console.error("Backend verification error:", error);
      return null;
    }
  };

  useEffect(() => {
    // S'assurer que auth est bien initialisé
    if (!auth) {
      console.error("Firebase auth is not initialized");
      setLoading(false);
      return;
    }

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      console.log('onAuthStateChanged called', { firebaseUser: firebaseUser?.email, originalUserExists: !!originalUser });
      
      if (firebaseUser) {
        try {
          // Vérifier avec le backend OBLIGATOIREMENT
          const backendUser = await verifyUserWithBackend(firebaseUser);
          
          // Si le backend ne répond pas, on ne peut pas authentifier correctement
          if (!backendUser) {
            console.error("Backend verification failed - cannot authenticate user");
            setCurrentUser(null);
            setLoading(false);
            return;
          }
          
          const authenticatedUser = backendUser;
          
          // NE PAS écraser currentUser si on est en mode impersonation
          // L'état d'impersonation doit être préservé
          if (!originalUser) {
            // Pas en mode impersonation, on peut mettre à jour currentUser normalement
            console.log('Not impersonating, setting currentUser to:', authenticatedUser);
            setCurrentUser(authenticatedUser);
          } else {
            console.log('Currently impersonating, NOT updating currentUser');
          }
          // Si originalUser existe, on est en impersonation et on ne touche pas à currentUser
        } catch (error) {
          console.error("Erreur lors de l'authentification:", error);
          // Seulement réinitialiser si pas en impersonation
          if (!originalUser) {
            setCurrentUser(null);
          }
        }
      } else {
        // Utilisateur déconnecté de Firebase
        console.log('User signed out from Firebase');
        setCurrentUser(null);
        setOriginalUser(null); // Réinitialiser aussi l'impersonation
      }
      setLoading(false);
    });

    // Cleanup subscription
    return () => unsubscribe();
  }, []); // Retirer originalUser des dépendances pour éviter les re-renders

  // Fonction de logout améliorée pour garantir une déconnexion complète
  const handleLogout = async (): Promise<void> => {
    try {
      console.log("AuthContext: Starting logout process");
      
      // Effacer d'abord l'état utilisateur local
      setCurrentUser(null);
      console.log("AuthContext: User state cleared locally");
      
      // Puis se déconnecter de Firebase
      try {
        console.log("AuthContext: Calling Firebase signOut");
        await firebaseLogout();
        console.log("AuthContext: Firebase signOut completed");
      } catch (error) {
        console.error("AuthContext: Firebase logout error:", error);
        // Même en cas d'erreur Firebase, on considère l'utilisateur déconnecté localement
      }
      
      // Force le nettoyage de tout stockage local potentiel
      localStorage.removeItem('firebase:authUser');
      sessionStorage.removeItem('firebase:authUser');
      console.log("AuthContext: Local storage cleaned");
    } catch (error) {
      console.error("AuthContext: Critical error during logout:", error);
      // Même en cas d'erreur, s'assurer que l'utilisateur est considéré comme déconnecté
      setCurrentUser(null);
    }
  };

  const value = {
    currentUser,
    originalUser,
    isImpersonating,
    setUser: setCurrentUser,
    loading,
    logout: handleLogout,
    startImpersonation,
    stopImpersonation,
  };

  console.log('🟪 AuthContext value:', { 
    hasStopImpersonation: typeof value.stopImpersonation,
    isImpersonating: value.isImpersonating 
  });

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};